import React, { useState, useRef, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholder from "../../assets/card_placeholder.png";
import fire from "../../assets/ic_fire.svg";
import diamond from "../../assets/ic_diamond.svg";
import DomainUtils from '../../services/DomainUtils';
import './AnimeCardComponent.css';

// Memoize the component to prevent unnecessary re-renders
const AnimeCardComponent = memo(({ anime, isInteractive = false }) => {
    const [state, setState] = useState({
        isHovered: false,
        imageLoaded: false,
        isVisible: false,
        imageSrc: placeholder // Start with placeholder
    });
    const navigate = useNavigate();
    const cardRef = useRef();
    
    // Combine image loading and visibility detection in one effect
    useEffect(() => {
        // Setup Intersection Observer
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setState(prev => ({ ...prev, isVisible: true }));
                    observer.unobserve(cardRef.current);
                    
                    // Only load image when visible
                    if (anime && anime.id) {
                        const API_DOMAIN = window.location.hostname === 'localhost' 
                            ? 'https://api.animix.club' 
                            : `https://api.${DomainUtils.getDomain()}`;
                        
                        const animeImageUrl = `${API_DOMAIN}/anime/getThumb/${anime.id}_thumb.png`;
                        
                        const img = new Image();
                        img.src = animeImageUrl;
                        img.onload = () => {
                            setState(prev => ({ 
                                ...prev, 
                                imageSrc: animeImageUrl,
                                imageLoaded: true 
                            }));
                        };
                        img.onerror = () => {
                            setState(prev => ({ 
                                ...prev, 
                                imageLoaded: true 
                            }));
                        };
                    }
                }
            },
            { threshold: 0.1, rootMargin: '200px' } // Preload images as they approach viewport
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, [anime]);

    const handleCardInteraction = (isEntering) => {
        if (!isInteractive) return;
        setState(prev => ({ ...prev, isHovered: isEntering }));
    };

    const handleNavigate = (e) => {
        e.preventDefault();
        navigate(`/anime/${anime.id}`);
    };

    const getDescription = () => {
        if (!anime || anime.description === undefined || anime.description === null) return '';
        return anime.description.length > 80
            ? anime.description.substring(0, 80) + "..."
            : anime.description;
    };

    // Destructure state for cleaner JSX
    const { isHovered, imageLoaded, imageSrc } = state;

    // Only render if we have anime data
    if (!anime) return null;

    return (
        <a
            className="anime-card"
            onClick={handleNavigate}
            onMouseEnter={() => handleCardInteraction(true)}
            onMouseLeave={() => handleCardInteraction(false)}
            ref={cardRef}
            href={`/anime/${anime.id}`}
            style={{ 
                willChange: 'transform, box-shadow',
                transform: isHovered ? 'translateY(5px)' : 'translateY(0)'
            }}
        >
            <img
                src={imageSrc}
                alt={anime.name}
                loading="lazy"
                className={`anime-thumbnail ${isHovered ? 'hovered' : ''} ${imageLoaded ? 'loaded' : ''}`}
                style={{ willChange: 'opacity, transform' }}
            />
            
            {anime.hq && (
                <img 
                    className={`hq ${isHovered ? 'hovered' : ''}`} 
                    src={diamond} 
                    alt="High quality"
                    loading="lazy" 
                />
            )}
            
            {isInteractive && (
                <div className="anime-info">
                    <div className='anime-text-container'>
                        <h3 className="anime-title-card">{anime.name}</h3>
                        <p className='anime-description'>{getDescription()}</p>
                    </div>
                    <button 
                        className='button-sec watch-button' 
                        onClick={handleNavigate}
                    >
                        Смотреть
                    </button>
                </div>
            )}
           
            <div className='badges'>
                {anime.viral > 10 && (
                    <img className="viral" src={fire} alt="viral" loading="lazy" />
                )}
                
                {anime.rating !== 0 && (
                    <h4 className={`rating ${
                        anime.rating >= 9 ? "premium" : 
                        anime.rating >= 7.5 ? "good" : ""
                    }`}>
                        ★ {anime.rating}
                    </h4>
                )}
            </div>
        </a>
    );
});

export default AnimeCardComponent;